import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import FabBar from "@/design/components/FabBar";
import MasterList from "@/design/components/MasterList";
import PageTitle from "@/design/components/PageTitle";
import { disableSearch, enableSearch } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viBuilding, viPlus } from "@sippy-platform/valkyrie";

import { Button, ListItem, ListItemAvatar, ListItemButton, ListItemText, useMediaQuery, useTheme } from "@mui/material";

import AccountFlyout from "./AccountFlyout";

function PbxList() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const auth = useSelector((state) => state.auth);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const pbx = useSelector((state) => state.domain.pbx);
  const accountStore = useSelector((state) => state.domain.accounts);

  // Search every time accountList or searchQuery changes
  const searchResults = useSearch(accountStore.list, searchValue, ["firstname", "lastname", "email"]);

  useEffect(() => {
    toggleLoadingState(true);
    Promise.all([Api.getPbx(), Api.getAccounts()]).finally(toggleLoadingState);
    if (auth.accessLevel <= 1) Promise.resolve(Api.getAllAccounts());
  }, [auth.accessLevel]);

  // Open first item or new if the viewport is large enough
  useEffect(() => {
    if (!id && matchesMdUp) {
      navigate("pbx");
    }
  }, [id, matchesMdUp, navigate]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <>
      <MasterList title="PBX" aria-label="PBX account list" id="pbx-list" fabBar>
        <ListItem disablePadding>
          <ListItemButton component={NavLink} to={`pbx`} selected={id === undefined && matchesMdUp}>
            <ListItemAvatar>
              <Avatar text={pbx.name} icon={viBuilding} />
            </ListItemAvatar>
            <ListItemText primary={pbx.name} secondary={pbx.context} />
          </ListItemButton>
        </ListItem>
        {auth.accessLevel <= 1 && (
          <>
            <PageTitle>Accounts</PageTitle>
            {searchResults.length > 0 ? (
              searchResults.map((account) => (
                <ListItem disablePadding key={account.id}>
                  <ListItemButton component={NavLink} to={`${account.id}`} selected={account.id == id}>
                    <ListItemAvatar>
                      <Avatar text={`${account.firstname} ${account.lastname ?? ""}`} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${account.firstname} ${account.lastname ?? ""}`}
                      secondary={account.email}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            ) : (
              <ListItem>
                <ListItemText primary="No accounts" />
              </ListItem>
            )}
          </>
        )}
      </MasterList>
      {auth.accessLevel <= 1 && (
        <FabBar>
          <AccountFlyout />
          <Button variant="contained" component={NavLink} to="new" startIcon={<Valkyrie icon={viPlus} />}>
            Add
          </Button>
        </FabBar>
      )}
    </>
  );
}

export default PbxList;
